import { Hyphenator } from './hyphenation';

// Czech vowels (including accented + ě)
const VOWELS = new Set(['a', 'e', 'i', 'o', 'u', 'y', 'á', 'é', 'í', 'ó', 'ú', 'ů', 'ý', 'ě']);

// Valid Czech word-initial 2-consonant clusters (maximum onset principle)
// Note: "ch"-initial entries are 3 chars but represent 2 consonant units (ch + one)
const VALID_ONSETS_2 = new Set([
  'bl',
  'br',
  'bř',
  'čl',
  'čr',
  'čt',
  'dl',
  'dn',
  'dr',
  'dř',
  'dv',
  'fl',
  'fr',
  'gl',
  'gr',
  'hl',
  'hm',
  'hn',
  'hr',
  'hř',
  'hv',
  'jm',
  'kl',
  'km',
  'kn',
  'kr',
  'kř',
  'kt',
  'kv',
  'ml',
  'mn',
  'mr',
  'mř',
  'pl',
  'pn',
  'pr',
  'př',
  'ps',
  'pt',
  'sl',
  'sm',
  'sn',
  'sp',
  'sr',
  'st',
  'sv',
  'šk',
  'šl',
  'šm',
  'šn',
  'šp',
  'šr',
  'šv',
  'št',
  'tl',
  'tr',
  'tř',
  'tv',
  'vl',
  'vr',
  'vř',
  'zl',
  'zm',
  'zn',
  'zr',
  'zř',
  'zt',
  'žl',
  'žr',
  'žv',
  // ch-initial onsets (ch = single consonant unit, so these are 2 consonant units)
  'chl',
  'chr',
  'chv',
  'chm',
]);

// Valid Czech word-initial 3-consonant clusters
const VALID_ONSETS_3 = new Set([
  'skl',
  'skr',
  'skř',
  'spl',
  'spr',
  'str',
  'stř',
  'stv',
  'špr',
  'štr',
]);

// Czech suffixes where a morphological break should be forced
const CZECH_SUFFIXES = ['nout'];
// Minimum stem length before suffix
const MIN_STEM_BEFORE_SUFFIX = 3;

// Tokenize a Czech word into units, treating the "ch" digraph as a single unit
function tokenize(str: string): string[] {
  const tokens: string[] = [];
  let i = 0;
  while (i < str.length) {
    if (str[i] === 'c' && i + 1 < str.length && str[i + 1] === 'h') {
      tokens.push('ch');
      i += 2;
    } else {
      tokens.push(str[i]);
      i += 1;
    }
  }
  return tokens;
}

interface VowelPos {
  pos: number; // index in original string
  len: number; // 1 for regular vowel, 2 for "ou" diphthong
}

// Get vowel positions, treating "ch" as consonant and "ou" as a single diphthong unit
function getVowelPositions(word: string): VowelPos[] {
  const result: VowelPos[] = [];
  let i = 0;
  while (i < word.length) {
    if (word[i] === 'c' && i + 1 < word.length && word[i + 1] === 'h') {
      // "ch" digraph — single consonant, skip both chars
      i += 2;
    } else if (VOWELS.has(word[i])) {
      if (word[i] === 'o' && i + 1 < word.length && word[i + 1] === 'u') {
        result.push({ pos: i, len: 2 });
        i += 2;
      } else {
        result.push({ pos: i, len: 1 });
        i += 1;
      }
    } else {
      i += 1;
    }
  }
  return result;
}

// Apply break positions to word, returning syllable parts
function applyBreaks(word: string, breaks: number[]): string[] {
  const parts: string[] = [];
  let last = 0;
  for (const pos of breaks) {
    parts.push(word.slice(last, pos));
    last = pos;
  }
  parts.push(word.slice(last));
  return parts.filter((p) => p.length > 0);
}

// Find valid onset length (in consonant units) for a cluster token array using maximum onset
function maxOnsetLen(tokens: string[]): number {
  if (tokens.length === 0) return 0;

  // Try 3-unit onset
  if (tokens.length >= 3) {
    const candidate = tokens.slice(tokens.length - 3).join('');
    if (VALID_ONSETS_3.has(candidate)) return 3;
  }
  // Try 2-unit onset
  if (tokens.length >= 2) {
    const candidate = tokens.slice(tokens.length - 2).join('');
    if (VALID_ONSETS_2.has(candidate)) return 2;
  }
  // Single consonant is always a valid onset
  return 1;
}

// Core Czech syllabification: returns break positions in the (lowercase) word
function syllabifyCzech(word: string): number[] {
  // Check suffix rules first (morphological boundary)
  for (const suffix of CZECH_SUFFIXES) {
    if (word.endsWith(suffix)) {
      const stem = word.slice(0, word.length - suffix.length);
      if (stem.length >= MIN_STEM_BEFORE_SUFFIX) {
        const stemBreaks = syllabifyCzech(stem);
        const suffixStart = stem.length;
        return [...stemBreaks, suffixStart];
      }
    }
  }

  const vowels = getVowelPositions(word);
  if (vowels.length < 2) return [];

  const breaks: number[] = [];

  for (let v = 0; v < vowels.length - 1; v++) {
    const v1 = vowels[v];
    const v2 = vowels[v + 1];

    const clusterStr = word.slice(v1.pos + v1.len, v2.pos);
    const clusterTokens = tokenize(clusterStr);

    let breakPos: number;
    if (clusterTokens.length === 0) {
      // Adjacent vowels — break between them
      breakPos = v1.pos + v1.len;
    } else {
      const onsetLen = maxOnsetLen(clusterTokens);
      const codaTokens = clusterTokens.slice(0, clusterTokens.length - onsetLen);
      const codaCharLen = codaTokens.reduce((sum, t) => sum + t.length, 0);
      breakPos = v1.pos + v1.len + codaCharLen;
    }

    // Enforce minimum 2 chars on each side
    if (breakPos >= 2 && breakPos <= word.length - 2) {
      breaks.push(breakPos);
    }
  }

  return breaks;
}

export class CzechHybridHyphenator extends Hyphenator {
  hyphenate(word: string): string[] {
    const cleanWord = word.replace(/[^\p{L}]/gu, '');
    if (cleanWord.length < 2) return [word];

    const breaks = syllabifyCzech(cleanWord.toLowerCase());
    if (breaks.length > 0) {
      return applyBreaks(word, breaks);
    }
    // TeX pattern fallback for loanwords / unusual clusters
    return super.hyphenate(word);
  }
}
